import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import apiClient, { getApiUrl } from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
// formatIndiaTime and getIndiaTime removed - using formatLocalTime instead
import { ChatSkeleton, ChatMessagesSkeleton } from '../components/Skeleton';
import { MessageCircle, Search, Send, RefreshCw, FileText, ChevronDown, X, Check, CheckCheck, Paperclip, ArrowLeft } from 'lucide-react';

// Utility function to normalize phone numbers (extract last 10 digits)
const normalizePhone = (phone) => {
  if (!phone) return null;
  // Remove all non-numeric characters and get last 10 digits
  const cleaned = phone.replace(/[^0-9]/g, '');
  return cleaned.slice(-10);
};

// Format time in user's local timezone
// The database stores timestamps in UTC (UTC+0)
// We need to parse them as UTC and let JavaScript automatically convert to local timezone
const formatLocalTime = (dateString, format = 'time-ampm') => {
  if (!dateString) return '';

  let date;

  // Handle different timestamp formats from the database
  if (typeof dateString === 'string') {
    // If it includes timezone info (Z or +00:00, +05:30, etc.), parse as-is
    if (dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/)) {
      date = new Date(dateString);
    } else {
      // Plain datetime string from database (e.g., '2025-01-15 14:30:00')
      // Database stores in UTC, so we need to parse it as UTC
      // Convert to ISO format and append 'Z' to indicate UTC
      const dateTimeStr = dateString.replace(' ', 'T');
      // If it doesn't have seconds/milliseconds, add them
      const isoString = dateTimeStr.includes('.') ? dateTimeStr + 'Z' : dateTimeStr + '.000Z';
      date = new Date(isoString);
    }
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) {
    // Fallback: try parsing as-is (might be a Date object or other format)
    date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
  }

  // Date header for chat separators (Today, Yesterday, or date - like WhatsApp)
  if (format === 'date-header') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const messageDate = new Date(date);
    messageDate.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - messageDate) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';

    return messageDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }

  if (format === 'relative') {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) {
      const hours = diffHours;
      const mins = diffMins % 60;
      return mins > 0 ? `${hours}h ${mins}m ago` : `${hours}h ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    // Show date for older messages - use local date comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const messageDate = new Date(date);
    messageDate.setHours(0, 0, 0, 0);
    const diffDaysLocal = Math.floor((today - messageDate) / 86400000);

    if (diffDaysLocal === 0) return 'Today';
    if (diffDaysLocal === 1) return 'Yesterday';

    return messageDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: messageDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }

  if (format === 'time-ampm') {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  if (format === 'time-only') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  // Default: full date and time
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  });
};

const Chats = () => {
  const { currentWorkspace } = useWorkspace();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Debug: Log every render
  console.log('🔍 Chats component render - Workspace ID:', currentWorkspace?.id, 'Name:', currentWorkspace?.name);
  const [filteredChats, setFilteredChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendInProgress, setSendInProgress] = useState(false); // Disables button (no loader for optimistic)
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const processedContactRef = useRef(null);
  // Suppress SSE updates for a chat right after we manually refresh it (to avoid duplicates)
  const sseIgnorePhoneRef = useRef(null);
  const sseIgnoreTimeoutRef = useRef(null);
  const blobUrlsToRevokeRef = useRef([]);

  // Message pagination for chat history
  const MESSAGE_PAGE_SIZE = 50;
  const [messagePages, setMessagePages] = useState(1);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const messageApiPageRef = useRef(1);
  const [hasMoreOlderMessages, setHasMoreOlderMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);

  // Only show the most recent N messages (pages * 50), oldest -> newest
  const visibleMessages = useMemo(() => {
    if (!messages || messages.length === 0) return [];
    const limit = MESSAGE_PAGE_SIZE * messagePages;
    const startIndex = Math.max(0, messages.length - limit);
    return messages.slice(startIndex);
  }, [messages, messagePages, MESSAGE_PAGE_SIZE]);

  // Pagination and filtering state
  const [chatPage, setChatPage] = useState(1); // API page for chats (10 per page)
  const [currentPage, setCurrentPage] = useState(1); // UI page within filtered list
  const [chatsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Mobile view state - show list on mobile, both on desktop
  const [showMobileChat, setShowMobileChat] = useState(false);

  // Template-related state
  const [selectedTemplateType, setSelectedTemplateType] = useState('stored');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [templateParams, setTemplateParams] = useState({});
  const [useTemplate, setUseTemplate] = useState(false);

  // Media upload state
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const fileInputRef = useRef(null);


  // 24-hour window state
  const [windowStatus, setWindowStatus] = useState({
    isActive: false,
    lastClientMessageAt: null,
    canSendDirect: false,
    canSendStoredTemplate: false,
    canSendFacebookTemplate: true
  });

  const [syncingTemplates, setSyncingTemplates] = useState(false);

  // Debounce search so we don't hit the server on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 400); // wait 400ms after user stops typing
    return () => clearTimeout(handle);
  }, [searchInput]);

  // When any filter is active, API fetches more and filters server-side; use page 1
  const hasActiveFilters = !!(searchTerm?.trim() || selectedGroup || selectedTag || showUnreadOnly);
  const apiPage = hasActiveFilters ? 1 : chatPage;

  // Fetch chats - server applies search/group/tag/unread filters when provided
  const { data: chatsData, isLoading: loading } = useQuery(
    ['chats', currentWorkspace?.id, apiPage, searchTerm, selectedGroup, selectedTag, showUnreadOnly],
    async () => {
      if (!currentWorkspace?.id) return { chats: [], pagination: { page: 1, limit: 10, total: 0, pages: 1 } };

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const params = { page: apiPage, limit: 10, messagesPerChat: 50 };
      if (searchTerm?.trim()) params.search = searchTerm.trim();
      if (selectedGroup) params.group = selectedGroup;
      if (selectedTag) params.tag = selectedTag;
      if (showUnreadOnly) params.unreadOnly = 'true';

      const response = await apiClient.get('/api/whatsapp/chats', { headers, params });

      const rawChats = response.data?.chats || [];
      const chats = rawChats.map(chat => ({
        ...chat,
        messages: [...(chat.messages || [])].reverse()
      }));
      return { chats, pagination: response.data?.pagination || { page: 1, limit: 10, total: 0, pages: 1 } };
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('Error loading chats:', error);
        toast.error('Failed to load chats');
      }
    }
  );

  // Lazy-load templates when user opens template dropdown
  const { data: templatesData } = useQuery(
    ['chat-templates', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return { templates: [], facebookTemplates: [] };
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const [templatesRes, facebookRes] = await Promise.all([
        apiClient.get('/api/templates', { params: { page: 1, limit: 50 }, headers }).catch(() => ({ data: { templates: [] } })),
        apiClient.get('/api/whatsapp/templates?days=365&useCache=true', { headers }).catch(() => ({ data: { templates: [] } }))
      ]);
      return {
        templates: templatesRes.data?.templates || [],
        facebookTemplates: facebookRes.data?.templates || []
      };
    },
    { enabled: !!currentWorkspace?.id && showTemplateDropdown, staleTime: 5 * 60 * 1000 }
  );

  // Lazy-load groups and tags when user opens filter dropdown
  const { data: filterData } = useQuery(
    ['chat-filters', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return { groups: [], tags: [] };
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const [groupsRes, tagsRes] = await Promise.all([
        apiClient.get('/api/contacts/groups/list', { headers }).catch(() => ({ data: { groups: [] } })),
        apiClient.get('/api/contacts/tags/list', { headers }).catch(() => ({ data: { tags: [] } }))
      ]);
      return {
        groups: groupsRes.data?.groups || [],
        tags: tagsRes.data?.tags || []
      };
    },
    { enabled: !!currentWorkspace?.id, staleTime: 5 * 60 * 1000 }
  );

  const templates = templatesData?.templates ?? [];
  const availableGroups = filterData?.groups ?? [];
  const availableTags = filterData?.tags ?? [];
  const facebookTemplates = templatesData?.facebookTemplates ?? [];

  // Helper function to clean messages - ensure they belong to the chat (defined before useEffect that uses it)
  const cleanChatMessages = useCallback((messages, chatPhoneNormalized) => {
    if (!messages || !Array.isArray(messages)) return [];

    return messages.filter(msg => {
      // Get phone numbers from message
      const fromPhone = normalizePhone(msg.from_number || '');
      const toPhone = normalizePhone(msg.to_number || '');

      // Message belongs to this chat if:
      // 1. from_number matches chat phone (incoming message)
      // 2. to_number matches chat phone (outgoing message)
      // 3. No phone numbers (legacy messages, keep them)
      const belongsToChat =
        fromPhone === chatPhoneNormalized ||
        toPhone === chatPhoneNormalized ||
        (!fromPhone && !toPhone);

      if (!belongsToChat) {
        console.warn('⚠️ Removing message that doesn\'t belong to chat:', {
          messageId: msg.id,
          messageText: msg.message_text?.substring(0, 30),
          fromPhone,
          toPhone,
          chatPhone: chatPhoneNormalized
        });
      }

      return belongsToChat;
    });
  }, []);

  // Keep local state for realtime updates
  const [chats, setChats] = useState([]);

  // Update local state when React Query data changes (paginated chats from API)
  useEffect(() => {
    if (chatsData?.chats) {
      setChats(chatsData.chats.map(chat => ({
        ...chat,
        messages: cleanChatMessages(chat.messages || [], normalizePhone(chat.phone) || '')
      })));
    }
  }, [chatsData, cleanChatMessages]);

  // Simple function to calculate 24-hour window status
  const calculateWindowStatus = (chat) => {
    if (!chat || !chat.messages || chat.messages.length === 0) {
      return {
        isActive: false,
        lastClientMessageAt: null,
        timeRemaining: 0,
        canSendDirect: false,
        canSendStoredTemplate: false,
        canSendFacebookTemplate: true
      };
    }

    // Find the last client message (incoming message)
    const clientMessages = chat.messages.filter(msg =>
      msg.type === 'whatsapp' &&
      msg.from_number &&
      !msg.is_outgoing
    );

    if (clientMessages.length === 0) {
      return {
        isActive: false,
        lastClientMessageAt: null,
        timeRemaining: 0,
        canSendDirect: false,
        canSendStoredTemplate: false,
        canSendFacebookTemplate: true
      };
    }

    // Get the most recent client message
    const lastClientMessage = clientMessages.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    )[0];

    // Simple calculation: last message time + 24 hours
    const lastMessageTime = new Date(lastClientMessage.created_at);
    const now = new Date();

    // Always calculate from the message time + 24 hours
    const expiryTime = new Date(lastMessageTime.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours
    const timeDiff = expiryTime - now;
    const hoursRemaining = Math.max(0, timeDiff / (1000 * 60 * 60));

    // Check if message is in the future (server time issue)
    const isMessageInFuture = lastMessageTime > now;

    if (isMessageInFuture) {
      console.log('⚠️ Message is in the future, but calculating normally');
    }

    // Manual verification calculation
    const manualElapsed = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
    const manualRemaining = Math.max(0, 24 - manualElapsed);

    console.log('🔍 Manual Verification:', {
      manualElapsed: manualElapsed,
      manualRemaining: manualRemaining,
      isMessageInFuture: isMessageInFuture,
      finalHoursRemaining: hoursRemaining,
      status: isMessageInFuture ? 'Future message - 24h window active' : 'Normal calculation'
    });

    // Debug logging with IST conversion
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const lastMessageIST = new Date(lastMessageTime.getTime() + istOffset);
    const nowIST = new Date(now.getTime() + istOffset);

    // Calculate elapsed time since message
    const elapsedTime = now.getTime() - lastMessageTime.getTime();
    const elapsedHours = elapsedTime / (1000 * 60 * 60);
    const expectedRemaining = Math.max(0, 24 - elapsedHours);

    console.log('🕐 24h Window Debug:', {
      rawMessageTime: lastClientMessage.created_at,
      lastMessageUTC: lastMessageTime.toISOString(),
      lastMessageIST: lastMessageIST.toISOString(),
      nowUTC: now.toISOString(),
      nowIST: nowIST.toISOString(),
      elapsedTime: elapsedTime,
      elapsedHours: elapsedHours,
      expectedRemaining: expectedRemaining,
      actualHoursRemaining: hoursRemaining,
      currentYear: now.getFullYear(),
      messageYear: lastMessageTime.getFullYear(),
      isMessageInFuture: isMessageInFuture
    });

    return {
      isActive: hoursRemaining > 0,
      lastClientMessageAt: lastMessageTime.toISOString(),
      canSendDirect: hoursRemaining > 0,
      canSendStoredTemplate: hoursRemaining > 0,
      canSendFacebookTemplate: true
    };
  };


  // Function to refresh current chat messages (with optional silent mode)
  const refreshCurrentChat = useCallback(async (silent = false) => {
    if (!selectedChat) return;

    try {
      if (!silent) {
        setLoadingMessages(true);
      }
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: {
          phoneNumber: selectedChat.phone,
          limit: 100,
          type: 'whatsapp'
        }
      });

      if (response.data?.messages) {
        // Merge with existing messages and deduplicate
        // IMPORTANT: Backend may return messages for multiple contacts.
        // We must filter strictly to the current chat's phone so that
        // other chats' messages (same template content) don't appear here.
        const currentPhoneNormalized = normalizePhone(selectedChat.phone) || '';

        setMessages(prevMessages => {
          // Remove optimistic (tmp-*) placeholders - API now has the real message
          const withoutOptimistic = prevMessages.filter(m => !String(m.id || '').startsWith('tmp-'));
          const combined = [...withoutOptimistic, ...response.data.messages];
          const allMessages = cleanChatMessages(combined, currentPhoneNormalized);

          // Remove duplicates - use Map for O(1) lookup
          const messagesMap = new Map();
          const seenIds = new Set();
          const seenContentKeys = new Set();

          allMessages.forEach(msg => {
            const msgId = msg.id;
            const msgTime = new Date(msg.created_at);
            const msgFrom = normalizePhone(msg.from_number || '');
            const msgTo = normalizePhone(msg.to_number || '');
            const msgText = msg.message_text || '';
            const msgMedia = msg.media_url || '';

            // First, check by ID (most reliable)
            if (msgId) {
              if (!seenIds.has(msgId)) {
                seenIds.add(msgId);
                messagesMap.set(`id-${msgId}`, msg);
              } else {
                // Same ID exists - prefer newer timestamp
                const existing = messagesMap.get(`id-${msgId}`);
                if (msgTime >= new Date(existing.created_at)) {
                  messagesMap.set(`id-${msgId}`, msg);
                }
              }
              return; // Skip content-based check if we have ID
            }

            // No ID - check by content + time + phones (with 2 second tolerance)
            let foundDuplicate = false;
            for (const [key, existingMsg] of messagesMap.entries()) {
              if (key.startsWith('id-')) continue; // Skip ID-based entries

              const existingTime = new Date(existingMsg.created_at);
              const timeDiff = Math.abs(msgTime - existingTime);

              // Check if within 2 second window
              if (timeDiff < 2000) {
                const existingFrom = normalizePhone(existingMsg.from_number || '');
                const existingTo = normalizePhone(existingMsg.to_number || '');
                const existingText = existingMsg.message_text || '';
                const existingMedia = existingMsg.media_url || '';

                // Check content match
                const contentMatch = (msgMedia && existingMedia && msgMedia === existingMedia) ||
                  (msgText && existingText && msgText === existingText && !msgMedia && !existingMedia);

                // Check phone match (bidirectional)
                const phoneMatch = (msgFrom === existingFrom && msgTo === existingTo) ||
                  (msgFrom === existingTo && msgTo === existingFrom);

                if (contentMatch && phoneMatch) {
                  // Found duplicate - prefer one with ID or newer timestamp
                  if (existingMsg.id && !msg.id) {
                    foundDuplicate = true;
                    break; // Keep existing with ID
                  } else if (msgTime > existingTime) {
                    messagesMap.delete(key);
                    const contentKey = `${msgMedia || msgText}-${msgTime.getTime()}-${msgFrom}-${msgTo}`;
                    messagesMap.set(contentKey, msg);
                    seenContentKeys.add(contentKey);
                    foundDuplicate = true;
                    break;
                  } else {
                    foundDuplicate = true;
                    break; // Keep existing
                  }
                }
              }
            }

            if (!foundDuplicate) {
              // Add as new message
              const contentKey = `${msgMedia || msgText}-${msgTime.getTime()}-${msgFrom}-${msgTo}`;
              if (!seenContentKeys.has(contentKey)) {
                seenContentKeys.add(contentKey);
                messagesMap.set(contentKey, msg);
              }
            }
          });

          const uniqueMessages = Array.from(messagesMap.values());
          // Sort oldest -> newest so our scroll-to-bottom logic shows the most recent messages
          const sortedMessages = uniqueMessages.sort((a, b) =>
            new Date(a.created_at) - new Date(b.created_at)
          );

          if (!silent) {
            console.log(`✅ Refreshed messages: ${sortedMessages.length} unique messages (merged ${prevMessages.length} existing + ${response.data.messages.length} new, removed ${allMessages.length - sortedMessages.length} duplicates)`);
          }

          // After a refresh, we want to show latest page and autoscroll
          setMessagePages(1);
          setAutoScrollEnabled(true);
          return sortedMessages;
        });
      }
    } catch (error) {
      console.error('Error refreshing chat messages:', error);
      if (!silent) {
        toast.error('Failed to refresh messages');
      }
    } finally {
      if (!silent) {
        setLoadingMessages(false);
      }
    }
  }, [selectedChat, currentWorkspace?.id, cleanChatMessages]);

  const loadMoreOlderMessages = useCallback(async () => {
    if (!selectedChat || !hasMoreOlderMessages || loadingOlderMessages) return;
    setLoadingOlderMessages(true);
    try {
      const nextPage = messageApiPageRef.current + 1;
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: { phoneNumber: selectedChat.phone, page: nextPage, limit: 50, type: 'whatsapp' }
      });
      const older = response.data?.messages || [];
      setHasMoreOlderMessages(older.length >= 50);
      messageApiPageRef.current = nextPage;
      if (older.length > 0) {
        const currentPhoneNorm = normalizePhone(selectedChat.phone) || '';
        const cleaned = cleanChatMessages(older, currentPhoneNorm);
        const reversed = [...cleaned].reverse();
        setMessages(prev => [...reversed, ...prev]);
        setAutoScrollEnabled(false);
      }
    } catch (err) {
      console.error('Load older messages error:', err);
      toast.error('Failed to load older messages');
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [selectedChat, hasMoreOlderMessages, loadingOlderMessages, currentWorkspace?.id, cleanChatMessages]);

  // Handle contact query parameter from URL (for auto-opening chat from email)
  useEffect(() => {
    if (!currentWorkspace || !chatsData?.chats || selectedChat) return;

    const searchParams = new URLSearchParams(location.search);
    const contactParam = searchParams.get('contact');

    if (contactParam) {
      // Normalize the contact phone for matching
      const normalizedContact = contactParam.replace(/[^0-9]/g, '').slice(-10);

      // Find matching chat
      const matchingChat = chatsData.chats.find(chat => {
        const chatNormalized = chat.normalizedPhone || chat.phone?.replace(/[^0-9]/g, '').slice(-10);
        return chatNormalized === normalizedContact;
      });

      if (matchingChat) {
        console.log(`📱 Auto-opening chat for contact: ${contactParam}`);
        setSelectedChat(matchingChat);
        setShowMobileChat(true); // Show chat view on mobile

        // Load messages for this chat
        const uniqueMessages = [];
        const seenMessages = new Set();
        (matchingChat.messages || []).forEach(msg => {
          const uniqueKey = msg.id
            ? `id-${msg.id}`
            : `${msg.media_url ? `media-${msg.media_url}` : `text-${msg.message_text || ''}`}-time-${msg.created_at}-from-${msg.from_number || ''}-to-${msg.to_number || ''}`;
          if (!seenMessages.has(uniqueKey)) {
            seenMessages.add(uniqueKey);
            uniqueMessages.push(msg);
          }
        });
        // Sort oldest -> newest so scroll-to-bottom shows latest messages
        const sortedMessages = uniqueMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(sortedMessages);
        setMessagePages(1);
        setAutoScrollEnabled(true);
        messageApiPageRef.current = 1;
        setHasMoreOlderMessages((matchingChat.messages?.length || 0) >= 50);

        const newWindowStatus = calculateWindowStatus(matchingChat);
        setWindowStatus(newWindowStatus);

        // Clear the query parameter after opening
        window.history.replaceState({}, '', '/chats');
      }
    }
  }, [location.search, currentWorkspace, chatsData?.chats, selectedChat]);

  // Function to select a chat
  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);
    // On mobile, show chat view and hide list
    setShowMobileChat(true);

    // Remove duplicates before displaying
    const uniqueMessages = [];
    const seenMessages = new Set();

    (chat.messages || []).forEach(msg => {
      // Create a unique key: id (if exists) or text/media + exact timestamp + phone
      // Time must match exactly - different times = different messages
      const uniqueKey = msg.id
        ? `id-${msg.id}`
        : `${msg.media_url ? `media-${msg.media_url}` : `text-${msg.message_text || ''}`}-time-${msg.created_at}-from-${msg.from_number || ''}-to-${msg.to_number || ''}`;

      if (!seenMessages.has(uniqueKey)) {
        seenMessages.add(uniqueKey);
        uniqueMessages.push(msg);
      }
    });

    const sortedMessages = uniqueMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    setMessages(sortedMessages);
    setMessagePages(1);
    setAutoScrollEnabled(true);
    messageApiPageRef.current = 1;
    setHasMoreOlderMessages((chat.messages?.length || 0) >= 50);

    // Calculate 24-hour window status for this chat
    const newWindowStatus = calculateWindowStatus(chat);
    setWindowStatus(newWindowStatus);

    // Mark messages as read when chat is selected
    if (chat.unread > 0) {
      try {
        // Update unread count in local state immediately for better UX
        setChats(prevChats =>
          prevChats.map(c =>
            c.id === chat.id ? { ...c, unread: 0 } : c
          )
        );

        // Also update filtered chats if they exist
        setFilteredChats(prevFiltered =>
          prevFiltered.map(c =>
            c.id === chat.id ? { ...c, unread: 0 } : c
          )
        );

        // Mark messages as read in the database
        await apiClient.post('/api/whatsapp/messages/mark-read', {
          phoneNumber: chat.phone
        }, {
          headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {}
        });

        console.log('✅ Marked messages as read for:', chat.phone);
      } catch (error) {
        console.error('Error marking messages as read:', error);
        // If the API call fails, we still want to keep the local state updated
        // The unread count will be corrected on the next reload
      }
    }

    // Force scroll to bottom when chat is selected
    setTimeout(() => {
      if (messagesContainerRef.current) {
        // Scroll the container to bottom
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      } else if (messagesEndRef.current) {
        // Fallback to scrollIntoView
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  // Auto-select chat when navigated from Contacts page
  useEffect(() => {
    // Only run if there's a contact to open AND data is loaded (not loading)
    if (!location.state?.openContact || loading || !chatsData) return;

    const contactPhone = location.state.openContact;
    const contactName = location.state.contactName;

    // Normalize phone numbers for comparison (last 10 digits)
    const targetPhoneLast10 = normalizePhone(contactPhone) || '';

    // Check if we've already processed this contact to prevent infinite loops
    if (processedContactRef.current === targetPhoneLast10) {
      console.log('🔄 Already processed this contact, skipping:', contactName, contactPhone);
      return;
    }

    // Use chatsData directly (source of truth) instead of local state
    const chatsList = chatsData.chats || [];

    console.log('🎯 Auto-selecting chat for contact:', contactName, contactPhone);
    console.log('   Chats from React Query:', chatsList.length, 'Loading:', loading);
    console.log('   Looking for normalized phone:', targetPhoneLast10);

    // Find the chat with matching phone number
    const chatToOpen = chatsList.find(chat => {
      const chatPhoneLast10 = normalizePhone(chat.phone);
      console.log('   Checking chat:', chat.name, 'normalized:', chatPhoneLast10);
      return chatPhoneLast10 === targetPhoneLast10;
    });

    if (chatToOpen) {
      console.log(`✅ Found existing chat for: ${contactName || contactPhone}`);
      console.log('   Messages in chat:', chatToOpen.messages?.length || 0);
      console.log('   Last message:', chatToOpen.lastMessage);

      // Make sure we use the chat from local state to keep proper reference
      const localChat = chats.find(c => normalizePhone(c.phone) === targetPhoneLast10) || chatToOpen;
      handleChatSelect(localChat);

      // Mark as processed
      processedContactRef.current = targetPhoneLast10;
    } else {
      // Only create new chat if data is fully loaded AND we haven't already created one
      const existingLocalChat = chats.find(c => normalizePhone(c.phone) === targetPhoneLast10);

      if (!existingLocalChat) {
        console.log(`📝 No chat history found, creating new chat for: ${contactName || contactPhone}`);
        const newChat = {
          id: contactPhone,
          name: contactName || contactPhone,
          phone: contactPhone,
          phoneNumber: contactPhone,
          normalizedPhone: targetPhoneLast10,
          messages: [],
          lastMessage: 'No messages yet',
          timestamp: 'New conversation',
          unread: 0,
          unreadCount: 0,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName || contactPhone)}&background=random`,
          group: '',
          tags: []
        };

        // Add to chats list at the top
        setChats(prevChats => [newChat, ...prevChats]);

        // Select the new chat
        handleChatSelect(newChat);

        // Mark as processed
        processedContactRef.current = targetPhoneLast10;

        console.log(`✅ Created and selected new chat for: ${contactName || contactPhone}`);
      } else {
        console.log(`✅ Using existing local chat for: ${contactName || contactPhone}`);
        handleChatSelect(existingLocalChat);

        // Mark as processed
        processedContactRef.current = targetPhoneLast10;
      }

    }

    // Clear the location state to prevent re-selecting on refresh
    window.history.replaceState({}, document.title);
  }, [chatsData, location.state, loading]); // Removed 'chats' from dependencies to prevent infinite loop

  // Reset processed contact ref when location state changes
  useEffect(() => {
    processedContactRef.current = null;
  }, [location.state?.openContact]);

  // Optimistic realtime update on new message (insert into UI without reload)
  useEffect(() => {
    console.log('🎯 Setting up SSE listener (useEffect triggered)');

    const onNewMessage = (e) => {
      const data = e.detail;
      console.log('🔔 Real-time message received:', data);

      if (data?.workspaceId && currentWorkspace?.id && data.workspaceId !== currentWorkspace.id) {
        console.log('❌ Wrong workspace, ignoring');
        return;
      }

      const phone = data?.fromNumber || data?.toNumber;
      if (!phone) {
        console.log('❌ No phone number in message');
        return;
      }

      // Use the actual created_at from database, fallback to current time
      const messageTimestamp = data.createdAt || new Date().toISOString();
      console.log('⏰ Message timestamp:', messageTimestamp, 'from server:', !!data.createdAt);

      // Look up contact information
      const contact = findContact(phone);
      console.log('🔍 Contact lookup result:', { phone, contact, hasContact: !!contact });

      const contactName = contact ? contact.name : phone;
      const contactGroup = contact ? contact.group_name : '';
      const contactTags = contact ? contact.tags : [];

      // Contact details processed

      const incoming = {
        id: `tmp-${Date.now()}`,
        type: 'whatsapp',
        from_number: data.fromNumber || null,
        to_number: data.toNumber || null,
        message_text: data.messageText || '',
        status: '',
        media_url: data.mediaUrl || null,
        media_type: data.mediaType || null,
        created_at: messageTimestamp,  // Use actual database timestamp
        contact_name: contactName,
      };

      // Normalize phone numbers for comparison (last 10 digits)
      const incomingPhoneLast10 = normalizePhone(phone) || '';

      console.log('📞 Incoming phone (last 10):', incomingPhoneLast10);
      console.log('📱 Selected chat phone:', selectedChat?.phone);
      console.log('📱 Selected chat phone (last 10):', selectedChat ? normalizePhone(selectedChat.phone) : 'none');

      // If we've just sent a message from this chat and are doing a manual refresh,
      // temporarily ignore SSE events for this phone to avoid duplicate inserts
      if (sseIgnorePhoneRef.current && sseIgnorePhoneRef.current === incomingPhoneLast10) {
        console.log('⏭️ Ignoring SSE message for phone due to recent manual refresh:', incomingPhoneLast10);
        return;
      }

      setChats(prev => {
        const exists = prev.find(c => normalizePhone(c.phone) === incomingPhoneLast10);
        let updatedChats;

        if (exists) {
          // Update existing chat and move it to the top
          // CRITICAL: Never replace a proper name with a phone number
          let finalName = exists.name;
          let finalGroup = exists.group;
          let finalTags = exists.tags;
          let finalAvatar = exists.avatar;

          if (contact) {
            // We have valid contact info - update if the name is not just a phone number
            if (contact.name && contact.name.trim() && contact.name.trim() !== phone) {
              finalName = contact.name.trim();
              // Only regenerate avatar if we have a proper name
              finalAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name.trim())}&background=random`;
            }
            if (contactGroup) {
              finalGroup = contactGroup;
            }
            if (contactTags && contactTags.length > 0) {
              finalTags = contactTags;
            }
          }
          // else: No contact found - keep ALL existing data (name, avatar, group, tags)

          console.log('📝 Chat update decision:', {
            existingName: exists.name,
            newContactName: contact?.name,
            finalNameUsed: finalName,
            avatarUpdated: finalAvatar !== exists.avatar,
            reason: contact ? 'Contact found' : 'No contact - keeping existing'
          });

          // CRITICAL: Verify that incoming message belongs to this chat
          // Filter out any existing messages that don't belong to this chat
          const existingMessages = (exists.messages || []).filter(msg => {
            const msgPhone = normalizePhone(msg.from_number || msg.to_number || '');
            return msgPhone === incomingPhoneLast10 || !msgPhone; // Keep if phone matches or no phone
          });

          // Ensure incoming message belongs to this chat
          const incomingMsgPhone = normalizePhone(incoming.from_number || incoming.to_number || '');
          if (incomingMsgPhone && incomingMsgPhone !== incomingPhoneLast10) {
            console.warn('⚠️ Message phone mismatch - skipping merge:', {
              incomingPhone: incomingMsgPhone,
              chatPhone: incomingPhoneLast10
            });
            return prev; // Don't update if phone doesn't match
          }

          const updatedChat = {
            ...exists,
            name: finalName,
            avatar: finalAvatar,
            group: finalGroup,
            tags: finalTags,
            messages: [incoming, ...existingMessages], // Only include messages that belong to this chat
            lastMessage: incoming.message_text || 'Media message',
            timestamp: 'just now',
            unread: (selectedChat && normalizePhone(selectedChat.phone) === incomingPhoneLast10) ? 0 : ((exists.unread || 0) + 1)
          };

          // Remove the existing chat and add it to the top
          const otherChats = prev.filter(c => normalizePhone(c.phone) !== incomingPhoneLast10);
          updatedChats = [updatedChat, ...otherChats];

          // Note: We don't update selectedChat here to avoid triggering re-renders
          // The selectedChat will be synced when the user interacts with it
        } else {
          // Create new chat at the top
          const newChat = {
            id: phone,
            name: contactName,
            phone,
            group: contactGroup,
            tags: contactTags,
            messages: [incoming],
            lastMessage: incoming.message_text || 'Media message',
            timestamp: 'just now',
            unread: (selectedChat && normalizePhone(selectedChat.phone) === incomingPhoneLast10) ? 0 : 1,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`
          };
          updatedChats = [newChat, ...prev];
        }

        // Don't call updateFilteredChats here - let the useEffect handle it
        // This prevents infinite loops when chats state changes

        return updatedChats;
      });

      // Silently refresh chat list after a short delay to get the latest data from DB
      // This ensures we have the correct message IDs, delivery status, and all metadata
      // Silent refresh means no loading spinners, no toasts - just smooth real-time updates
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: ['chats', currentWorkspace?.id],
          type: 'active'
        }, {
          // Silent refetch - no loading states, no error toasts
          throwOnError: false
        }).catch(err => {
          // Silently handle errors - don't show toasts for background refreshes
          console.error('Silent chat list refresh error:', err);
        });
      }, 1000);

      // Update messages in chat area if this chat is currently selected
      // Use normalized phone comparison to handle different formats
      if (selectedChat && normalizePhone(selectedChat.phone) === incomingPhoneLast10) {
        console.log('✅ Updating messages in chat area for selected chat');
        setMessages(prevMsgs => {
          // Strict duplicate detection: requires exact time match (within 2 seconds)
          // Different times = different messages, even if text is the same
          const messageExists = prevMsgs.some(m => {
            // Check by ID first (most reliable)
            if (m.id && incoming.id && m.id === incoming.id) {
              return true;
            }

            // Check by exact timestamp (within 2 second tolerance for network delays and template processing)
            const timeDiff = Math.abs(new Date(m.created_at) - new Date(incoming.created_at));
            const timeMatch = timeDiff < 2000; // 2 second tolerance for templates

            if (!timeMatch) {
              // Different times = different messages, not duplicates
              return false;
            }

            // If time matches, check content and phones
            // Normalize phone numbers for comparison
            const mFromPhone = normalizePhone(m.from_number || '');
            const mToPhone = normalizePhone(m.to_number || '');
            const incomingFromPhone = normalizePhone(incoming.from_number || '');
            const incomingToPhone = normalizePhone(incoming.to_number || '');

            const contentMatch = (m.media_url && incoming.media_url && m.media_url === incoming.media_url) ||
              (m.message_text === incoming.message_text && !m.media_url && !incoming.media_url);

            const phoneMatch = (mFromPhone === incomingFromPhone && mToPhone === incomingToPhone) ||
              (mFromPhone === incomingToPhone && mToPhone === incomingFromPhone);

            // Message is duplicate ONLY if time matches AND content matches AND phones match
            return timeMatch && contentMatch && phoneMatch;
          });

          if (messageExists) {
            console.log('⚠️ Duplicate message detected, skipping:', {
              text: incoming.message_text,
              timestamp: incoming.created_at,
              reason: 'Already exists in messages array'
            });
            return prevMsgs;
          }

          console.log('📝 Adding new message:', incoming.message_text, 'at', incoming.created_at);

          // Add new message and sort by timestamp (oldest first for proper display)
          const updatedMessages = [incoming, ...prevMsgs];
          const sorted = updatedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

          console.log('✅ Messages updated. Total:', sorted.length);

          // Scroll to bottom after adding new message
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          }, 100);

          return sorted;
        });

        // Silently refresh individual chat messages after a short delay to get the real message from DB
        // This ensures we have the correct message ID and delivery status
        setTimeout(() => {
          refreshCurrentChat(true); // Silent refresh
        }, 1000);

        // Recalculate window status if this is a client message
        if (incoming.from_number && !incoming.is_outgoing) {
          const updatedChat = {
            ...selectedChat,
            messages: [incoming, ...(selectedChat.messages || [])]
          };
          const newWindowStatus = calculateWindowStatus(updatedChat);
          setWindowStatus(newWindowStatus);
        }
      } else {
        console.log('ℹ️ Chat not selected or phone mismatch');
      }
    };

    window.addEventListener('sse:new-message', onNewMessage);
    console.log('✅ SSE listener attached');

    return () => {
      window.removeEventListener('sse:new-message', onNewMessage);
      console.log('🔴 SSE listener removed (cleanup)');
    };
  }, [currentWorkspace, selectedChat, refreshCurrentChat, queryClient]);

  // Update filtered chats with given chat list
  const updateFilteredChats = useCallback((chatList) => {
    let filtered = [...chatList];

    // Filter by search term (name or phone)
    if (searchTerm) {
      filtered = filtered.filter(chat => {
        const name = chat.name || '';
        const phone = chat.phone || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm);
      });
    }

    // Filter by group (if contact has group info)
    if (selectedGroup) {
      const gf = selectedGroup.trim().toLowerCase();
      filtered = filtered.filter(chat => {
        const g = (chat.group || '').trim().toLowerCase();
        if (gf === 'no group') {
          return !g || g === 'from messages';
        }
        return g === gf;
      });
    }

    // Filter by tag (if contact has tag info)
    if (selectedTag) {
      const tf = selectedTag.trim().toLowerCase();
      filtered = filtered.filter(chat => {
        if (tf === 'no tags') {
          return !Array.isArray(chat.tags) || chat.tags.length === 0;
        }
        return Array.isArray(chat.tags) &&
          chat.tags.some(t => String(t || '').toLowerCase() === tf);
      });
    }

    // Filter by unread status
    if (showUnreadOnly) {
      filtered = filtered.filter(chat => chat.unread > 0);
    }

    // Sort by most recent message first (independent of filters - always recalc for SSE/optimistic updates)
    const getLatestTime = (chat) => {
      const fromObj = chat.lastMessageObj?.created_at;
      if (fromObj) {
        const t = new Date(fromObj).getTime();
        if (!Number.isNaN(t)) return t;
      }
      if (chat.messages?.length) {
        const times = chat.messages.map(m => new Date(m.created_at).getTime()).filter(t => !Number.isNaN(t));
        if (times.length) return Math.max(...times);
      }
      return 0;
    };
    filtered.sort((a, b) => {
      const ta = getLatestTime(a);
      const tb = getLatestTime(b);
      return tb - ta || 0; // stable: most recent first
    });

    setFilteredChats(filtered);
  }, [searchTerm, selectedGroup, selectedTag, showUnreadOnly]);

  // Filter chats based on search term, group, and tag
  const filterChats = useCallback(() => {
    updateFilteredChats(chats);
  }, [chats, updateFilteredChats]);

  // Filter chats when search term, group, tag, or unread filter changes
  useEffect(() => {
    // Only filter if we have chats to filter
    if (chats.length > 0) {
      filterChats();
    }
  }, [filterChats]);

  // Reset to first page when filters or chat page change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGroup, selectedTag, showUnreadOnly, chatPage]);

  // Auto-scroll to bottom when visible messages change (new sends / refresh)
  useEffect(() => {
    if (!autoScrollEnabled) return;

    // Use setTimeout to ensure DOM is updated before scrolling
    const scrollToBottom = () => {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          // Scroll the container to bottom
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        } else if (messagesEndRef.current) {
          // Fallback to scrollIntoView
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    };

    scrollToBottom();
  }, [visibleMessages, autoScrollEnabled]);

  // Whenever we change chats, reset to show only the latest page of messages
  useEffect(() => {
    setMessagePages(1);
    setAutoScrollEnabled(true);
  }, [selectedChat?.phone]);

  // Sync selectedChat with updated chats list to ensure it always has the latest data
  // But don't update messages state here - let refreshCurrentChat handle that to avoid duplicates
  useEffect(() => {
    if (selectedChat && chats.length > 0) {
      const selectedChatPhoneNormalized = normalizePhone(selectedChat.phone);
      const updatedChat = chats.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
      if (updatedChat) {
        // Check if non-message fields have changed (don't sync messages here to avoid duplicates)
        const lastMessageChanged = updatedChat.lastMessage !== selectedChat.lastMessage;
        const unreadChanged = updatedChat.unread !== selectedChat.unread;
        const nameChanged = updatedChat.name !== selectedChat.name;
        const avatarChanged = updatedChat.avatar !== selectedChat.avatar;

        // Only update selectedChat if non-message fields changed
        // Messages will be synced via refreshCurrentChat() when needed
        if (lastMessageChanged || unreadChanged || nameChanged || avatarChanged) {
          // Update selectedChat but don't update messages state here
          // Messages are managed separately via refreshCurrentChat()
          setSelectedChat(prev => ({
            ...updatedChat,
            // Keep existing messages in selectedChat for reference, but don't sync to messages state
            messages: prev.messages || updatedChat.messages
          }));
        }
      }
    }
  }, [chats, selectedChat?.phone]);

  // Contact lookup for SSE new messages - returns null (contact info comes from messages API on next refresh)
  const findContact = () => null;

  // Get current page chats
  const getCurrentPageChats = () => {
    const startIndex = (currentPage - 1) * chatsPerPage;
    const endIndex = startIndex + chatsPerPage;
    return filteredChats.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    return Math.ceil(filteredChats.length / chatsPerPage);
  };

  // Pagination helpers for chat list (sidebar)
  const goToChatPage = (page) => {
    const totalPages = getTotalPages();
    const safePage = Math.min(Math.max(1, page), totalPages || 1);
    setCurrentPage(safePage);
  };

  const nextChatPage = () => {
    goToChatPage(currentPage + 1);
  };

  const prevChatPage = () => {
    goToChatPage(currentPage - 1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedGroup('');
    setSelectedTag('');
    setShowUnreadOnly(false);
    setCurrentPage(1);
  };

  // DEPRECATED: fetchTemplates removed - templates are now loaded via React Query (useQuery hook)
  // Templates are automatically loaded in the 'chats' query (lines 170-411)

  // DEPRECATED: loadMessages_REMOVED function removed - now using React Query for data loading
  // All message loading logic is handled in the useQuery hook above (lines 170-411)

  const handleSendMessage = async () => {
    if (!selectedChat || (!message.trim() && !selectedTemplate && !selectedMedia)) return;

    // Check 24-hour window for media messages
    if (selectedMedia && !windowStatus.canSendDirect) {
      toast.error('Cannot send media: 24-hour window expired. Only Facebook templates are allowed.');
      return;
    }

    const selectedChatPhoneNormalized = normalizePhone(selectedChat.phone);
    let messageContent = '';
    let tempId = `tmp-${Date.now()}`;

    const addOptimisticAndReset = (content) => {
      if (!content) return;
      const optimisticMsg = {
        id: tempId,
        message_text: content,
        to_number: selectedChat.phone,
        from_number: null,
        created_at: new Date().toISOString(),
        status: 'sent',
        delivery_status: 'sent',
        is_outgoing: true
      };
      setMessages(prev => [...prev, optimisticMsg].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      setMessagePages(1);
      setAutoScrollEnabled(true);
      setMessage('');
      setSelectedTemplate(null);
      setTemplateParams({});
      setUseTemplate(false);
      setShowTemplateDropdown(false);
      setSelectedMedia(null);
      setMediaPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const now = new Date().toISOString();
      const displayForList = content || 'Media message';
      const patch = { unread: 0, lastMessage: displayForList, timestamp: 'just now', lastMessageObj: { created_at: now } };
      setChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
      setFilteredChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
    };

    const addOptimisticMediaAndReset = (file, caption) => {
      const blobUrl = URL.createObjectURL(file);
      blobUrlsToRevokeRef.current.push(blobUrl);
      const mime = (file.type || '').toLowerCase();
      let mediaType = 'document';
      if (mime.startsWith('image/')) mediaType = 'image';
      else if (mime.startsWith('video/')) mediaType = 'video';
      else if (mime.startsWith('audio/')) mediaType = 'audio';
      const optimisticMsg = {
        id: tempId,
        message_text: caption || '',
        media_url: blobUrl,
        media_type: mediaType,
        to_number: selectedChat.phone,
        from_number: null,
        created_at: new Date().toISOString(),
        status: 'sent',
        delivery_status: 'sent',
        is_outgoing: true
      };
      setMessages(prev => [...prev, optimisticMsg].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
      setMessagePages(1);
      setAutoScrollEnabled(true);
      setMessage('');
      setSelectedTemplate(null);
      setTemplateParams({});
      setUseTemplate(false);
      setShowTemplateDropdown(false);
      setSelectedMedia(null);
      setMediaPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const displayForList = caption?.trim() || 'Media message';
      const now = new Date().toISOString();
      const patch = { unread: 0, lastMessage: displayForList, timestamp: 'just now', lastMessageObj: { created_at: now } };
      setChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
      setFilteredChats(prev => {
        const cur = prev.find(c => normalizePhone(c.phone) === selectedChatPhoneNormalized);
        if (!cur) return prev;
        const others = prev.filter(c => normalizePhone(c.phone) !== selectedChatPhoneNormalized);
        return [{ ...cur, ...patch }, ...others];
      });
    };

    try {

        if (useTemplate && selectedTemplate) {
          // Send template via send-message (single recipient - no need for send-bulk)
          const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

          if (selectedTemplateType === 'stored') {
            // For stored templates, use plain text mode with parameter substitution
            messageContent = selectedTemplate.content;
            Object.keys(templateParams).forEach((param, index) => {
              const placeholder = `{{${index + 1}}}`;
              messageContent = messageContent.replace(new RegExp(placeholder, 'g'), templateParams[param]);
            });
            setSendInProgress(true);
            addOptimisticAndReset(messageContent);
            await apiClient.post('/api/whatsapp/send-message', {
              phoneNumber: selectedChat.phone,
              messageText: messageContent
            }, { headers });
          } else {
            // For Facebook templates, use WhatsApp template mode
            const bodyComponent = selectedTemplate.components?.find(c => c.type === 'BODY');
            const placeholderOrder = [];
            if (bodyComponent?.text) {
              const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || [];
              matches.forEach((m) => {
                const idx = Number(m.replace(/[^0-9]/g, ''));
                if (!Number.isNaN(idx)) placeholderOrder.push(idx);
              });
            }

            const templateParameters = placeholderOrder.map((n) => {
              const value = templateParams[`param${n}`] || '';
              return { type: 'text', text: value };
            });

            if (placeholderOrder.length > 0 && templateParameters.some(p => !p.text || !`${p.text}`.trim())) {
              toast.error('Please fill all required template parameters.');
              return;
            }

            // Build full preview (rendered text with params) for DB - like bulk/public API
            messageContent = bodyComponent?.text || `[Template: ${selectedTemplate.name}]`;
            placeholderOrder.forEach((n) => {
              messageContent = messageContent.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), templateParams[`param${n}`] || '');
            });
            setSendInProgress(true);
            addOptimisticAndReset(messageContent);
            const payload = {
              phoneNumber: selectedChat.phone,
              templateName: selectedTemplate.name,
              templateLanguage: selectedTemplate.language || 'en_US',
              messageText: messageContent
            };
            if (templateParameters.length > 0) payload.templateParameters = templateParameters;
            await apiClient.post('/api/whatsapp/send-message', payload, { headers });
          }
        } else {
          // Send regular message (with or without media)
          let mediaUrl = null;
          let mediaType = null;
          let whatsappType = null;
          let apiMessageText = message.trim();

          if (selectedMedia) {
            // Optimistic: show media instantly (like text), then upload + send in background
            const mediaCaption = message.trim();
            apiMessageText = mediaCaption;
            setSendInProgress(true);
            addOptimisticMediaAndReset(selectedMedia, mediaCaption);
            try {
              const formData = new FormData();
              formData.append('media', selectedMedia);
              const uploadResponse = await apiClient.post('/api/whatsapp/upload-media', formData, {
                headers: { 'x-workspace-id': currentWorkspace?.id || '', 'Content-Type': 'multipart/form-data' }
              });
              mediaUrl = uploadResponse.data.filePath;
              mediaType = uploadResponse.data.mediaType;
              whatsappType = uploadResponse.data.whatsappType;
            } catch (uploadError) {
              toast.error('Failed to upload media. Please try again.');
              setMessages(prev => prev.filter(m => m.id !== tempId));
              blobUrlsToRevokeRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) {} });
              blobUrlsToRevokeRef.current = [];
              setSendInProgress(false);
              return;
            }
          } else {
            // Text message - optimistic add instantly (WhatsApp-like)
            messageContent = message.trim();
            apiMessageText = messageContent;
            setSendInProgress(true);
            addOptimisticAndReset(messageContent);
          }

          await apiClient.post('/api/whatsapp/send-message', {
            phoneNumber: selectedChat.phone,
            messageText: apiMessageText,
            mediaUrl: mediaUrl,
            mediaType: mediaType,
            whatsappType: whatsappType
          }, { headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {} });
        }

        // After sending from this chat, temporarily ignore SSE messages for this phone
        // while we perform a manual refresh from the server. This prevents the same
        // message from being added once via SSE and again via the API refresh.
        if (selectedChatPhoneNormalized) {
          sseIgnorePhoneRef.current = selectedChatPhoneNormalized;
          if (sseIgnoreTimeoutRef.current) {
            clearTimeout(sseIgnoreTimeoutRef.current);
          }
          sseIgnoreTimeoutRef.current = setTimeout(() => {
            sseIgnorePhoneRef.current = null;
            sseIgnoreTimeoutRef.current = null;
          }, 5000); // 5 seconds is enough for refresh + server processing
        }

        // Wait a bit for the server to process and then refresh messages
        // Don't invalidate queries immediately to avoid duplicate messages from SSE + refetch
        // Use a longer delay for templates to ensure server has processed the message
        const refreshDelay = useTemplate ? 2500 : 2000;
        setTimeout(async () => {
          try {
            // Refresh current chat messages to get latest from server (with deduplication)
            // This will merge with existing messages and remove duplicates
            await refreshCurrentChat(true); // Silent refresh to avoid loading flicker

            // Then invalidate and refetch the chats query to update the chat list
            // This ensures we get the latest chat order and metadata from server
            await queryClient.refetchQueries({
              queryKey: ['chats', currentWorkspace?.id],
              type: 'active'
            });
          } catch (error) {
            console.error('Error refreshing chats after send:', error);
            queryClient.invalidateQueries(['chats', currentWorkspace?.id]);
          } finally {
            setLoadingMessages(false);
            setAutoScrollEnabled(true);
            blobUrlsToRevokeRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) {} });
            blobUrlsToRevokeRef.current = [];
          }
        }, refreshDelay);
      } catch (error) {
        console.error('Send message error:', error.response?.data);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        blobUrlsToRevokeRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (_) {} });
        blobUrlsToRevokeRef.current = [];
        const errorMessage = error.response?.data?.error || error.response?.data?.details?.error?.message || 'Failed to send message';
        toast.error(`Send failed: ${errorMessage}`);
      } finally {
        setSending(false);
        setSendInProgress(false);
      }
  };

  // Helper function to format date headers (Today, Yesterday, or date - like WhatsApp)
  const formatDateHeader = (date) => {
    return formatLocalTime(date, 'date-header');
  };


  // Helper function to group messages by date (using user's local timezone - like WhatsApp)
  const groupMessagesByDate = (messages) => {
    const groups = {};

    messages.forEach(message => {
      // Use LOCAL date for grouping (user's timezone), not UTC
      const date = new Date(message.created_at);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: new Date(year, parseInt(month, 10) - 1, parseInt(day, 10)),
          dateKey: dateKey,
          dateHeader: formatDateHeader(message.created_at),
          messages: []
        };
      }

      groups[dateKey].messages.push(message);
    });

    // Convert to array and sort by date (oldest date first)
    const sortedGroups = Object.values(groups).sort((a, b) => a.date - b.date);

    // Sort messages within each group by timestamp (oldest first = ascending, like WhatsApp)
    sortedGroups.forEach(group => {
      group.messages.sort((a, b) => {
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        return timeA - timeB;
      });
    });

    return sortedGroups;
  };


  // Template handling functions
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setUseTemplate(true);
    setShowTemplateDropdown(false);

    // Initialize template parameters
    const params = {};
    if (template.parameters && Array.isArray(template.parameters)) {
      template.parameters.forEach(param => {
        params[param] = '';
      });
    } else if (template.components) {
      // Extract parameters from Facebook template components
      const bodyComponent = template.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const paramMatches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
        if (paramMatches) {
          paramMatches.forEach((match) => {
            const paramNumber = match.replace(/[^0-9]/g, '');
            params[`param${paramNumber}`] = '';
          });
        }
      }
    } else {
      // For stored templates, check for {{1}}, {{2}} patterns
      if (template.content) {
        const paramMatches = template.content.match(/\{\{(\d+)\}\}/g);
        if (paramMatches) {
          paramMatches.forEach((match) => {
            const paramNumber = match.replace(/[^0-9]/g, '');
            params[`param${paramNumber}`] = '';
          });
        }
      }
    }
    setTemplateParams(params);
  };

  const handleTemplateTypeChange = (type) => {
    setSelectedTemplateType(type);
    setSelectedTemplate(null);
    setTemplateParams({});
  };

  const getCurrentTemplates = () => {
    return selectedTemplateType === 'stored' ? templates : facebookTemplates;
  };


  const renderTemplatePreview = () => {
    if (!selectedTemplate) return null;

    let content = selectedTemplate.content || selectedTemplate.name;

    // For Facebook templates, extract content from components
    if (selectedTemplate.components) {
      const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        content = bodyComponent.text;
      }
    }

    // Replace parameters in content
    Object.keys(templateParams).forEach((param, index) => {
      if (selectedTemplate.components) {
        // Facebook template format: {{1}}, {{2}}, etc.
        const placeholder = `{{${index + 1}}}`;
        content = content.replace(new RegExp(placeholder, 'g'), templateParams[param] || `[${param}]`);
      } else {
        // Stored template format: {param}
        const placeholder = `{${param}}`;
        content = content.replace(new RegExp(placeholder, 'g'), templateParams[param] || `[${param}]`);
      }
    });

    return (
      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-blue-800">
            Template Preview ({selectedTemplateType === 'stored' ? 'Stored' : 'Facebook'}):
          </span>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setUseTemplate(false);
              setTemplateParams({});
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-blue-700">{content}</p>
        <p className="text-xs text-blue-600 mt-1">
          Template: {selectedTemplate.name} | Status: {selectedTemplate.status || 'Unknown'}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-12rem)]">
        <ChatSkeleton />
      </div>
    );
  }

  return (
    <div className="h-[92vh] flex flex-col md:flex-row bg-gray-50 -m-4 sm:-m-6 lg:-m-8 p-0 sm:p-4 md:p-6 lg:p-8">
      {/* Chat List Sidebar - Hidden on mobile when chat is selected, always visible on desktop */}
      <div className={`${showMobileChat ? 'hidden' : 'flex'} md:flex w-full md:w-1/3 h-full bg-white border-r border-gray-200 flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">Chats</h1>

            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              />
            </div>

            {/* Filter Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                Filters
              </button>

              {(searchTerm || selectedGroup || selectedTag || showUnreadOnly) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Results Count & Chat Pagination */}
          <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm text-gray-500 font-medium">
              {getCurrentPageChats().length} of {filteredChats.length} chats
              {chatsData?.pagination?.total > 0 && ` (${chatsData.pagination.total} total)`}
            </span>
            {chatsData?.pagination && chatsData.pagination.pages > 1 && !hasActiveFilters && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setChatPage(p => Math.max(1, p - 1))}
                  disabled={chatPage <= 1 || loading}
                  className="px-2 py-1 text-xs font-medium border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <span className="text-xs text-gray-600">
                  Page {chatPage} of {chatsData.pagination.pages}
                </span>
                <button
                  onClick={() => setChatPage(p => Math.min(chatsData.pagination.pages, p + 1))}
                  disabled={chatPage >= chatsData.pagination.pages || loading}
                  className="px-2 py-1 text-xs font-medium border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Filter Dropdowns */}
          {showFilters && (
            <div className="mt-3 grid grid-cols-1 gap-3 p-4 bg-gray-50 rounded-xl">
              {/* Group Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Groups</option>
                  {availableGroups.map((group, index) => (
                    <option key={typeof group === 'string' ? group : `group-${index}`} value={typeof group === 'string' ? group : group.name || group}>
                      {typeof group === 'string' ? group : group.name || group}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Tags</option>
                  {availableTags.map((tag, index) => (
                    <option key={typeof tag === 'string' ? tag : `tag-${index}`} value={typeof tag === 'string' ? tag : tag.name || tag}>
                      {typeof tag === 'string' ? tag : tag.name || tag}
                    </option>
                  ))}
                </select>
              </div>

              {/* Unread Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unread Chats ({chats.filter(chat => chat.unread > 0).length})
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="unread-filter"
                    checked={showUnreadOnly}
                    onChange={(e) => setShowUnreadOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="unread-filter" className="text-sm text-gray-700">
                    Show only unread chats
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {getCurrentPageChats().map((chat) => (
            <div
              key={chat.id}
              onClick={() => handleChatSelect(chat)}
              className={`p-4 cursor-pointer transition-all duration-200 border-l-4 ${selectedChat?.id === chat.id
                ? 'bg-blue-50 border-blue-500 shadow-sm'
                : 'hover:bg-gray-50 border-transparent'
                }`}
            >
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={chat.avatar}
                    alt={chat.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  {chat.unread > 0 && (
                    <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {chat.unread > 99 ? '99+' : chat.unread}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {chat.name}
                    </h3>
                    <span className="text-xs text-gray-500 font-medium">{chat.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate leading-relaxed">{chat.lastMessage}</p>
                  {chat.group && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {chat.group}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Chat list pagination (20 per page) */}
          {getTotalPages() > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-600">
              <button
                onClick={prevChatPage}
                disabled={currentPage === 1}
                className={`px-3 py-1 rounded-lg border ${currentPage === 1
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 hover:bg-gray-100'
                  }`}
              >
                Previous
              </button>

              <span className="text-[11px]">
                Page {currentPage} of {getTotalPages()} &middot;{' '}
                {filteredChats.length} chats
              </span>

              <button
                onClick={nextChatPage}
                disabled={currentPage === getTotalPages()}
                className={`px-3 py-1 rounded-lg border ${currentPage === getTotalPages()
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 hover:bg-gray-100'
                  }`}
              >
                Next
              </button>
            </div>
          )}

          {/* No chats message */}
          {getCurrentPageChats().length === 0 && !loading && (
            <div className="p-8 text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No chats found</p>
              {(searchTerm || selectedGroup || selectedTag || showUnreadOnly) && (
                <p className="text-sm text-gray-500 mt-2">Try adjusting your filters</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages Area - Hidden on mobile when no chat selected, always visible on desktop */}
      <div className={`${!showMobileChat && !selectedChat ? 'hidden' : 'flex'} md:flex flex-1 w-full md:w-2/3 h-full flex-col bg-white`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-3 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                {/* Back button for mobile */}
                <button
                  onClick={() => {
                    setShowMobileChat(false);
                    setSelectedChat(null);
                  }}
                  className="md:hidden mr-3 p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Back to chat list"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <div className="flex items-center space-x-3 flex-1">
                  <img
                    src={selectedChat.avatar}
                    alt={selectedChat.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{selectedChat.name}</h3>
                    <p className="text-xs text-gray-500">{selectedChat.phone}</p>
                    {selectedChat.group && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                        {selectedChat.group}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {loadingMessages ? (
                <ChatMessagesSkeleton />
              ) : (
                <div className="space-y-6">
                  {/* Load older messages: from memory or fetch from API */}
                  {(messages.length > visibleMessages.length || hasMoreOlderMessages) && (
                    <div className="flex justify-center">
                      <button
                        onClick={async () => {
                          if (messages.length > visibleMessages.length) {
                            setAutoScrollEnabled(false);
                            setMessagePages(prev => Math.min(prev + 1, Math.ceil(messages.length / MESSAGE_PAGE_SIZE)));
                          } else if (hasMoreOlderMessages) {
                            await loadMoreOlderMessages();
                          }
                        }}
                        disabled={loadingOlderMessages}
                        className="px-4 py-1 text-xs rounded-full border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                      >
                        {loadingOlderMessages ? 'Loading...' : 'Load older messages'}
                      </button>
                    </div>
                  )}

                  {visibleMessages.length > 0 ? (
                    (() => {
                      // Group messages by date (will be sorted within groups)
                      const groupedMessages = groupMessagesByDate(visibleMessages);

                      return groupedMessages.map((group, groupIndex) => (
                        <div key={groupIndex} className="space-y-3">
                          {/* Date Header */}
                          <div className="flex justify-center">
                            <div className="bg-white border border-gray-200 text-gray-600 text-xs px-4 py-2 rounded-full shadow-sm">
                              {group.dateHeader}
                            </div>
                          </div>

                          {/* Messages for this date */}
                          {group.messages.map((msg, index) => {
                            // Determine if message is outgoing (sent by us) or incoming (received from customer)
                            const isOutgoing = msg.type === 'outgoing' ||
                              (msg.to_number && !msg.from_number) ||
                              msg.status === 'sent';

                            // Get delivery status for tick marks (sent, delivered, read)
                            const deliveryStatus = msg.delivery_status || (msg.status === 'sent' ? 'sent' : null);

                            // Determine tick mark color and icon
                            let tickIcon = null;
                            let tickColor = '';
                            if (isOutgoing && deliveryStatus) {
                              if (deliveryStatus === 'read') {
                                tickIcon = <CheckCheck style={{ width: '1.275rem', height: '1.275rem' }} />;
                                tickColor = 'text-blue-600'; // Dark blue for read
                              } else if (deliveryStatus === 'delivered') {
                                tickIcon = <CheckCheck style={{ width: '1.275rem', height: '1.275rem' }} />;
                                tickColor = 'text-blue-400'; // Light blue for delivered
                              } else if (deliveryStatus === 'sent') {
                                tickIcon = <Check style={{ width: '1.275rem', height: '1.275rem' }} />;
                                tickColor = 'text-blue-400'; // Light blue for sent
                              }
                            }

                            // Get API base URL for media files (blob URLs for optimistic display as-is)
                            const mediaUrl = msg.media_url
                              ? (String(msg.media_url).startsWith('blob:') ? msg.media_url : `${getApiUrl()}${msg.media_url}`)
                              : null;
                            // Detect media type from URL if not provided
                            let mediaType = msg.media_type;
                            if (!mediaType && mediaUrl) {
                              const urlLower = mediaUrl.toLowerCase();
                              if (urlLower.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
                                mediaType = 'image';
                              } else if (urlLower.match(/\.(mp4|3gp|webm)$/)) {
                                mediaType = 'video';
                              } else if (urlLower.match(/\.(mp3|aac|m4a|amr|ogg|wav)$/)) {
                                mediaType = 'audio';
                              } else {
                                mediaType = 'document';
                              }
                            }

                            // Debug: Log media info for troubleshooting
                            if (msg.media_url) {
                              console.log('📷 Media info:', {
                                media_url: msg.media_url,
                                mediaUrl,
                                media_type: msg.media_type,
                                detectedType: mediaType,
                                hasMediaUrl: !!mediaUrl,
                                hasMediaType: !!mediaType
                              });
                            }

                            return (
                              <div key={`${groupIndex}-${index}`} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                                <div className={`px-4 py-3 rounded-2xl max-w-xs lg:max-w-md break-words shadow-sm ${isOutgoing
                                  ? 'text-white rounded-br-md'
                                  : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                                  }`} style={isOutgoing ? { backgroundColor: '#042b12' } : {}}>

                                  {/* Display Media */}
                                  {mediaUrl && (
                                    <div className="mb-2">
                                      {/* Images - check by mediaType or file extension */}
                                      {(mediaType === 'image' || mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                                        <div className="relative group">
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block cursor-pointer"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              window.open(mediaUrl, '_blank');
                                            }}
                                          >
                                            <img
                                              src={mediaUrl}
                                              alt={msg.message_text || 'Image'}
                                              className="max-w-full h-auto rounded-lg hover:opacity-90 transition-opacity"
                                              onError={(e) => {
                                                console.error('❌ Failed to load image:', mediaUrl);
                                                // Hide image and show fallback
                                                e.target.style.display = 'none';
                                                const container = e.target.closest('.relative');
                                                if (container) {
                                                  const fallback = container.querySelector('.image-fallback');
                                                  if (fallback) {
                                                    fallback.style.display = 'flex';
                                                  }
                                                }
                                              }}
                                              title="Click to open image in new tab"
                                            />
                                          </a>
                                          {/* Fallback link if image fails to load */}
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="image-fallback hidden items-center gap-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer group"
                                            style={{ display: 'none' }}
                                            onClick={(e) => {
                                              e.preventDefault();
                                              window.open(mediaUrl, '_blank');
                                            }}
                                          >
                                            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium text-gray-700 truncate group-hover:text-blue-600 transition-colors">
                                                Image
                                              </p>
                                              <p className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors">
                                                Click to open in new tab
                                              </p>
                                            </div>
                                            <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                          </a>
                                          {/* Hover overlay - make it clickable too */}
                                          <div
                                            className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              window.open(mediaUrl, '_blank');
                                            }}
                                          >
                                            <span className="text-white text-xs font-medium bg-black bg-opacity-50 px-2 py-1 rounded">
                                              Click to open
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                      {(mediaType === 'video' || (!mediaType && mediaUrl.match(/\.(mp4|3gp|webm)$/i))) && (
                                        <div className="relative group">
                                          <video
                                            src={mediaUrl}
                                            controls
                                            className="max-w-full h-auto rounded-lg"
                                            style={{ maxHeight: '300px' }}
                                          >
                                            Your browser does not support the video tag.
                                          </video>
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs hover:bg-opacity-70 transition-all"
                                            title="Open video in new tab"
                                          >
                                            Open
                                          </a>
                                        </div>
                                      )}
                                      {/* Audio - check by mediaType or file extension */}
                                      {(mediaType === 'audio' || mediaUrl.match(/\.(mp3|aac|m4a|amr|ogg|wav)$/i)) && (
                                        <div className="relative">
                                          <audio
                                            src={mediaUrl}
                                            controls
                                            className="w-full"
                                          >
                                            Your browser does not support the audio tag.
                                          </audio>
                                          <a
                                            href={mediaUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs hover:bg-opacity-70 transition-all"
                                            title="Open audio in new tab"
                                          >
                                            Open
                                          </a>
                                        </div>
                                      )}
                                      {/* Documents - everything else that's not image/video/audio */}
                                      {(mediaType === 'document' || (!mediaType && !mediaUrl.match(/\.(jpg|jpeg|png|gif|webp|mp4|3gp|webm|mp3|aac|m4a|amr|ogg|wav)$/i))) && (
                                        <a
                                          href={mediaUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer group"
                                          title="Click to open document in new tab"
                                        >
                                          <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                          </svg>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-700 truncate group-hover:text-blue-600 transition-colors">
                                              {msg.message_text || 'Document'}
                                            </p>
                                            <p className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors">
                                              Click to open in new tab
                                            </p>
                                          </div>
                                          <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                  )}

                                  {/* Display Text (caption or message) */}
                                  {msg.message_text && (
                                    <p className="text-sm leading-relaxed">{msg.message_text}</p>
                                  )}

                                  {/* Display placeholder for media without caption */}
                                  {mediaUrl && !msg.message_text && !mediaType && (
                                    <p className="text-sm leading-relaxed opacity-75">Media message</p>
                                  )}

                                  <div className={`flex items-center justify-end gap-1 mt-2 ${isOutgoing ? 'text-white' : 'text-gray-500'}`}>
                                    <span className={`text-xs ${isOutgoing ? 'text-white/90' : 'text-gray-500'}`}>
                                      {formatLocalTime(msg.created_at, 'time-ampm')}
                                    </span>
                                    {tickIcon && (
                                      <span className={tickColor}>
                                        {tickIcon}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="text-center text-gray-500 mt-8">
                      <MessageCircle className="mx-auto h-8 w-8 text-gray-300" />
                      <p className="mt-2">No messages yet</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              {/* 24-Hour Window Status */}
              {selectedChat && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${windowStatus.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm font-medium text-gray-700">
                        {windowStatus.isActive ? '24h Window Active' : '24h Window Expired'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    {windowStatus.isActive ? (
                      <span>✅ Direct messages and stored templates enabled</span>
                    ) : (
                      <span>⚠️ Only Facebook templates allowed</span>
                    )}
                  </div>
                </div>
              )}

              {/* Template Selection */}
              <div className="mb-3">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                    disabled={!windowStatus.canSendStoredTemplate && !windowStatus.canSendFacebookTemplate}
                    className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Send Template</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>


                  {showTemplateDropdown && (
                    <div className="flex-1 relative">
                      <select
                        value={selectedTemplateType}
                        onChange={(e) => handleTemplateTypeChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="stored" disabled={!windowStatus.canSendStoredTemplate}>
                          💾 Stored Templates {!windowStatus.canSendStoredTemplate ? '(Disabled)' : ''}
                        </option>
                        <option value="facebook">
                          📘 Facebook Templates
                        </option>
                      </select>
                    </div>
                  )}
                </div>

                {showTemplateDropdown && (
                  <div className="mt-2">
                    <select
                      value={selectedTemplate ? (selectedTemplate.id || selectedTemplate.name) : ''}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        const template = getCurrentTemplates().find(t =>
                          (t.id && t.id.toString() === templateId) || t.name === templateId
                        );
                        if (template) {
                          handleTemplateSelect(template);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Select a template...</option>
                      {getCurrentTemplates().map((template) => (
                        <option key={template.id || template.name} value={template.id || template.name}>
                          {template.name}
                          {template.status && ` [${template.status}]`}
                          {template.language && ` (${template.language})`}
                        </option>
                      ))}
                    </select>
                    {getCurrentTemplates().length === 0 && (
                      <div className="mt-2">
                        {selectedTemplateType === 'stored' ? (
                          <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-2">
                            💡 No stored templates available. Create templates in the Templates section.
                          </p>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <p className="text-xs text-yellow-800 mb-2">
                              ⚠️ No Facebook templates cached yet.
                            </p>
                            <button
                              onClick={async () => {
                                setSyncingTemplates(true);
                                try {
                                  const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                                  toast.loading('Syncing Facebook templates...');
                                  const syncResponse = await apiClient.post('/api/whatsapp/templates/sync', {}, { headers });
                                  if (syncResponse.data.success) {
                                    toast.dismiss();
                                    toast.success(`Synced ${syncResponse.data.saved} templates successfully`);
                                    await queryClient.invalidateQueries(['chat-templates', currentWorkspace?.id]);
                                  }
                                } catch (error) {
                                  toast.dismiss();
                                  toast.error(error.response?.data?.error || 'Failed to sync templates');
                                } finally {
                                  setSyncingTemplates(false);
                                }
                              }}
                              disabled={syncingTemplates}
                              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50 transition-all shadow-md"
                            >
                              <RefreshCw className={`h-4 w-4 ${syncingTemplates ? 'animate-spin' : ''}`} />
                              <span>{syncingTemplates ? 'Syncing...' : 'Sync Templates from Facebook'}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Template Parameters */}
              {selectedTemplate && Object.keys(templateParams).length > 0 && (
                <div className="mb-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Template Parameters:</p>
                  {Object.keys(templateParams).map(param => (
                    <input
                      key={param}
                      type="text"
                      value={templateParams[param]}
                      onChange={(e) => setTemplateParams({
                        ...templateParams,
                        [param]: e.target.value
                      })}
                      placeholder={`Enter ${param}`}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                    />
                  ))}
                </div>
              )}


              {/* Template Preview */}
              {renderTemplatePreview()}

              {/* Media Preview */}
              {mediaPreview && (
                <div className="px-4 pt-2 pb-0">
                  <div className="relative inline-block max-w-xs">
                    {mediaPreview.type.startsWith('image/') ? (
                      <img
                        src={mediaPreview.url}
                        alt="Preview"
                        className="max-w-full max-h-48 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg">
                        <FileText className="h-8 w-8 text-gray-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-700 truncate">
                            {selectedMedia?.name || 'File'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(selectedMedia?.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setSelectedMedia(null);
                        setMediaPreview(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Message Input */}
              <div className="flex items-end space-x-2 p-4 bg-white border-t border-gray-200">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Check 24-hour window
                      if (!windowStatus.canSendDirect && !useTemplate) {
                        toast.error('Cannot attach media: 24-hour window expired. Only Facebook templates are allowed.');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        return;
                      }
                      // Check file size (16MB limit)
                      if (file.size > 16 * 1024 * 1024) {
                        toast.error('File size must be less than 16MB');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                        return;
                      }
                      setSelectedMedia(file);
                      // Create preview URL
                      if (file.type.startsWith('image/')) {
                        setMediaPreview({
                          type: file.type,
                          url: URL.createObjectURL(file)
                        });
                      } else {
                        setMediaPreview({
                          type: file.type,
                          url: null
                        });
                      }
                    }
                  }}
                  className="hidden"
                />
                <button
                  onClick={() => {
                    if (!windowStatus.canSendDirect && !useTemplate) {
                      toast.error('Cannot attach media: 24-hour window expired. Only Facebook templates are allowed.');
                      return;
                    }
                    fileInputRef.current?.click();
                  }}
                  disabled={!windowStatus.canSendDirect && !useTemplate}
                  className={`p-3 rounded-2xl transition-colors ${!windowStatus.canSendDirect && !useTemplate
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  title={!windowStatus.canSendDirect && !useTemplate ? "24-hour window expired - Cannot attach media" : "Attach file"}
                >
                  <Paperclip className="h-5 w-5" />
                </button>
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={
                    !windowStatus.canSendDirect
                      ? "Only Facebook templates allowed - 24h window expired"
                      : useTemplate
                        ? "Or type a regular message..."
                        : "Type a message..."
                  }
                  className={`flex-1 px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${!windowStatus.canSendDirect
                    ? 'border-red-300 bg-red-50 text-red-500 cursor-not-allowed'
                    : 'border-gray-200 bg-gray-50'
                    }`}
                  disabled={(!windowStatus.canSendDirect && !useTemplate) || (useTemplate && selectedTemplate)}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={
                    sending ||
                    sendInProgress ||
                    (!message.trim() && !selectedTemplate && !selectedMedia) ||
                    (!windowStatus.canSendDirect && !useTemplate && !selectedTemplate) ||
                    (selectedMedia && !windowStatus.canSendDirect && !useTemplate) ||
                    (useTemplate && selectedTemplateType === 'stored' && !windowStatus.canSendStoredTemplate)
                  }
                  className={`p-3 rounded-2xl transition-colors shadow-sm flex items-center justify-center min-w-[44px] ${(!windowStatus.canSendDirect && !useTemplate && !selectedTemplate) ||
                    (selectedMedia && !windowStatus.canSendDirect && !useTemplate) ||
                    (useTemplate && selectedTemplateType === 'stored' && !windowStatus.canSendStoredTemplate)
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'
                    }`}
                >
                  {sending ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No chat selected</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Choose a chat from the list to start messaging and view conversation history
              </p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Chats;

